import React, { useCallback } from 'react';
import { DatePicker } from '@atlaskit/datetime-picker';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { DateReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/date/index.tsx';
import {
	getDefaultAttributeValue,
	useDefaultAttributeDateValue,
	useDefaultAttributeValue,
	getDefaultAttributeDateValue,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { EditViewSpacers } from '../../common/ui/edit-view-spacers/styled.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: DefaultAttributeType;
};

const DateAttributeOld = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);

	const [value, setValue] = useDefaultAttributeValue(attribute);

	const onValueChange = useCallback(
		(val: string) => {
			setValue(val);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getDefaultAttributeValue(attribute)),
		[attribute, setValue],
	);
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.date-attribute"
			value={value}
			onChange={onValueChange}
			storedValue={getDefaultAttributeValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<DateReadView value={value} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<EditViewSpacers.DatePicker>
					<DatePicker
						testId="servicedesk-insight-inline-editable-attributes.ui.date-attribute.date-picker"
						autoFocus
						locale={standardizedLocale}
						value={value}
						onChange={onChange}
						isInvalid={hasValidationError}
					/>
				</EditViewSpacers.DatePicker>
			)}
			onResetValue={resetValue}
		/>
	);
};

const DateAttributeNew = (props: Props) => {
	const { attributeType, isEditable } = props;

	const [{ attribute }] = useEditableAttribute(attributeType);

	const [value, setValue] = useDefaultAttributeDateValue(attribute);

	const onValueChange = useCallback(
		(val: string) => {
			setValue(val);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getDefaultAttributeDateValue(attribute)),
		[attribute, setValue],
	);
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.date-attribute"
			value={value}
			onChange={onValueChange}
			storedValue={getDefaultAttributeDateValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<DateReadView value={value} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<EditViewSpacers.DatePicker>
					<DatePicker
						testId="servicedesk-insight-inline-editable-attributes.ui.date-attribute.date-picker"
						autoFocus
						locale={standardizedLocale}
						value={value}
						onChange={onChange}
						isInvalid={hasValidationError}
					/>
				</EditViewSpacers.DatePicker>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const DateEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightInlineEditableDateAttribute"
		packageName="jiraServicedeskInsightEditableDateAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		{fg('fly-2238-invalid-date') ? (
			<DateAttributeNew {...props} />
		) : (
			<DateAttributeOld {...props} />
		)}
	</JSErrorBoundary>
);
